/**
 * More from Birdie - Product Recommendations Section
 * Handles color swatch interactions and product navigation
 */

class MoreFromBirdie {
  constructor() {
    this.init();
  }

  init() {
    this.bindColorSwatchEvents();
    this.bindNavigationEvents();
    this.bindShopNowEvents();
    this.initializeDefaultVariants();
    this.initializeCurrentlyViewingBadge();
  }

  bindColorSwatchEvents() {
    document.addEventListener('change', (e) => {
      const input = e.target.closest('.birdie-product-card__color-input');
      if (!input) return;

      const swatch = input.closest('.color-swatch');
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();
      
      const variantId = input.dataset.variantId;
      
      if (variantId) {
        // Update the product image based on variant
        this.updateProductImage(input, variantId);
      }
    });

    // Also handle clicks on labels for better UX
    document.addEventListener('click', (e) => {
      const label = e.target.closest('label[for^="card-"]');
      if (!label) return;

      const input = document.getElementById(label.getAttribute('for'));
      if (!input || !input.classList.contains('birdie-product-card__color-input')) return;

      // Trigger the change event
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  bindNavigationEvents() {
    // Handle keyboard navigation
    document.addEventListener('keydown', (e) => {
      const slider = e.target.closest('.more-from-birdie__slider');
      if (!slider) return;

      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.navigatePrevious(slider);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.navigateNext(slider);
          break;
      }
    });
  }

  updateProductImage(input, variantId) {
    const productCard = input.closest('.birdie-product-card');
    if (!productCard) return;

    // Check if variant is available
    const isAvailable = input.dataset.available === 'true';
    if (!isAvailable) return;

    // Remove active class from all swatches in this card
    const allSwatches = productCard.querySelectorAll('.color-swatch');
    allSwatches.forEach(s => s.classList.remove('checked'));
    
    // Add active class to selected swatch
    const swatch = input.closest('.color-swatch');
    swatch.classList.add('checked');

    // Get image URL and alt text from input data attributes
    const imageUrl = input.dataset.imageUrl;
    const imageAlt = input.dataset.imageAlt;
    const productImage = productCard.querySelector('.birdie-product-card__image img');
    
    if (productImage && imageUrl) {
      // Check if this is the same image already displayed
      const currentSrc = productImage.src.split('?')[0];
      const newSrc = imageUrl.split('?')[0];
      
      if (currentSrc === newSrc) {
        // Same image, just update the price and return
        this.updateVariantPrice(productCard, variantId);
        return;
      }
      
      // Add loading state only if image is different
      productImage.style.opacity = '0.5';
      
      // Create new image to preload
      const newImage = new Image();
      newImage.onload = () => {
        // Generate responsive srcset for the variant image
        const baseUrl = imageUrl.split('?')[0]; // Remove existing query parameters
        const responsiveSrcset = [
          `${baseUrl}?v=1698933914&width=165 165w`,
          `${baseUrl}?v=1698933914&width=360 360w`,
          `${baseUrl}?v=1698933914&width=533 533w`,
          `${baseUrl}?v=1698933914&width=720 720w`,
          `${baseUrl}?v=1698933914&width=940 940w`,
          `${baseUrl}?v=1698933914 1015w`
        ].join(', ');
        
        // Update both src and srcset to ensure the image updates
        productImage.src = imageUrl;
        productImage.srcset = responsiveSrcset;
        productImage.alt = imageAlt;
        productImage.style.opacity = '1';
      };
      newImage.onerror = () => {
        // If image fails to load, restore original
        productImage.style.opacity = '1';
      };
      newImage.src = imageUrl;
    }

    // Update price if variant has different price
    this.updateVariantPrice(productCard, variantId);

    // Store the selected variant ID for the Shop Now button
    const shopNowButton = productCard.querySelector('.birdie-product-card__button');
    if (shopNowButton) {
      shopNowButton.dataset.selectedVariantId = variantId;
    }
  }

  updateVariantPrice(productCard, variantId) {
    const priceElement = productCard.querySelector('.birdie-product-card__price-text');
    if (!priceElement) return;

    // Get variant data from the card
    const variantDataScript = productCard.querySelector('.birdie-product-card__variant-data');
    if (!variantDataScript) return;

    const variantData = JSON.parse(variantDataScript.textContent);
    const selectedVariant = variantData.find(variant => variant.id == variantId);
    
    if (!selectedVariant) return;

    // Format price using Shopify's money format
    const price = (selectedVariant.price / 100).toFixed(2);
    
    // Check if this product has multiple variants (color swatches)
    const hasColorSwatches = productCard.querySelector('.birdie-product-card__color-swatches');
    
    // Update price display
    if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
      // Show sale price
      if (hasColorSwatches) {
        priceElement.innerHTML = `Starting at <span class="price-sale">$${price}</span> <span class="price-compare">$${(selectedVariant.compare_at_price / 100).toFixed(2)}</span>`;
      } else {
        priceElement.innerHTML = `<span class="price-sale">$${price}</span> <span class="price-compare">$${(selectedVariant.compare_at_price / 100).toFixed(2)}</span>`;
      }
    } else {
      // Show regular price
      if (hasColorSwatches) {
        priceElement.textContent = `Starting at $${price}`;
      } else {
        priceElement.textContent = `$${price}`;
      }
    }
  }

  bindShopNowEvents() {
    document.addEventListener('click', (e) => {
      const shopNowButton = e.target.closest('.birdie-product-card__button');
      if (!shopNowButton) return;

      e.preventDefault();
      
      const productCard = shopNowButton.closest('.birdie-product-card');
      const productLink = productCard.querySelector('.birdie-product-card__image-link');
      const selectedVariantId = shopNowButton.dataset.selectedVariantId;
      
      if (productLink) {
        let productUrl = productLink.href;
        
        // Add variant parameter if a color was selected
        if (selectedVariantId) {
          const url = new URL(productUrl, window.location.origin);
          url.searchParams.set('variant', selectedVariantId);
          productUrl = url.toString();
        }
        
        // Navigate to the product page
        window.location.href = productUrl;
      }
    });
  }

  initializeDefaultVariants() {
    // Find all product cards and initialize their default variants
    const productCards = document.querySelectorAll('.birdie-product-card');
    
    productCards.forEach(card => {
      // Find the first checked color input
      const firstCheckedInput = card.querySelector('.birdie-product-card__color-input:checked');
      
      if (firstCheckedInput) {
        const variantId = firstCheckedInput.dataset.variantId;
        if (variantId) {
          // Initialize the product image and price for the default variant
          this.updateProductImage(firstCheckedInput, variantId);
          
          // Also update the Shop Now button with the selected variant
          const shopNowButton = card.querySelector('.birdie-product-card__button');
          if (shopNowButton) {
            shopNowButton.dataset.selectedVariantId = variantId;
          }
        }
      } else {
        // If no variant is checked, check the first available variant
        const firstAvailableInput = card.querySelector('.birdie-product-card__color-input[data-available="true"]');
        if (firstAvailableInput) {
          firstAvailableInput.checked = true;
          const variantId = firstAvailableInput.dataset.variantId;
          if (variantId) {
            this.updateProductImage(firstAvailableInput, variantId);
            
            // Update the Shop Now button
            const shopNowButton = card.querySelector('.birdie-product-card__button');
            if (shopNowButton) {
              shopNowButton.dataset.selectedVariantId = variantId;
            }
          }
        }
      }
    });
  }

  navigatePrevious(slider) {
    const prevBtn = slider.querySelector('.more-from-birdie__nav--prev');
    if (prevBtn && !prevBtn.disabled) {
      prevBtn.click();
    }
  }

  navigateNext(slider) {
    const nextBtn = slider.querySelector('.more-from-birdie__nav--next');
    if (nextBtn && !nextBtn.disabled) {
      nextBtn.click();
    }
  }

  initializeCurrentlyViewingBadge() {
    // Get current product handle from URL
    const currentPath = window.location.pathname;
    const currentHandle = currentPath.split('/').pop().split('?')[0];
    
    // Find all product cards
    const productCards = document.querySelectorAll('.birdie-product-card');
    
    productCards.forEach(card => {
      const productLink = card.querySelector('.birdie-product-card__image-link');
      if (!productLink) return;
      
      const productUrl = productLink.href;
      const productHandle = productUrl.split('/').pop().split('?')[0];
      
      // Check if this is the current product
      if (productHandle === currentHandle) {
        // Remove any existing badge first
        const existingBadge = card.querySelector('.birdie-product-card__currently-viewing');
        if (existingBadge) {
          existingBadge.remove();
        }
        
        // Add the "Currently Viewing" badge
        const badge = document.createElement('div');
        badge.className = 'birdie-product-card__currently-viewing';
        badge.innerHTML = '<span class="birdie-product-card__currently-viewing-text">Currently Viewing</span>';
        
        // Insert at the beginning of the card
        card.insertBefore(badge, card.firstChild);
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Add a small delay to ensure all images are loaded
  setTimeout(() => {
    new MoreFromBirdie();
  }, 100);
});

// Also initialize when window loads (fallback)
window.addEventListener('load', () => {
  // Only initialize if not already done
  if (!window.moreFromBirdieInstance) {
    window.moreFromBirdieInstance = new MoreFromBirdie();
  }
});

// Export for potential external use
window.MoreFromBirdie = MoreFromBirdie;
